/**
 * AllRoutesMap — the multi-technician route board map.
 *
 * Renders every visible technician's route on one Leaflet map, each as a real
 * driving path (Mapbox Directions) in the technician's color. Selecting a
 * technician focuses them (others dim) and exposes turn-by-turn directions.
 *
 * Reuses the heavy lifting from RouteWithDirections (`RoutingControl`,
 * `MapBoundsCalculator`) — one colored RoutingControl per visible route.
 */
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { MapContainer, TileLayer } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Navigation } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { RoutingControl, MapBoundsCalculator } from './RouteWithDirections';
import { StaticRoutePolyline } from './StaticRoutePolyline';
import { NavigationDirectionsDialog, RouteInstructions } from './NavigationDirectionsDialog';
import type { BoardRoute } from '../utils/routeBoardMapping';
import type { DealerLocation, TechnicianRoute } from '../types';

/**
 * When this few routes are visible, render them all as live (rich, numbered,
 * turn-by-turn-capable) directions — the cost is small. Above it, only the
 * focused route runs live directions; the rest are drawn statically from their
 * stored geometry to keep large boards responsive.
 */
const LIVE_ALL_THRESHOLD = 3;

interface AllRoutesMapProps {
  routes: BoardRoute[];
  /** Technician whose route is focused (others dim); null = all equal. */
  focusedTechnicianId?: string | null;
  /** Tenant default start location, used when a route has no stored start. */
  fallbackBaseLocation?: { lat: number; lng: number } | null;
  /** Reports computed road distance/duration per route once directions load. */
  onRouteMetrics?: (routeId: string, distanceMiles: number, durationMinutes: number) => void;
}

const FLORIDA_CENTER: [number, number] = [27.9944024, -81.7602544];

/** Compute a reasonable initial center from all routes' stops. */
function computeCenter(routes: BoardRoute[]): [number, number] {
  const lats: number[] = [];
  const lngs: number[] = [];
  routes.forEach(route =>
    route.stops.forEach(stop => {
      const { latitude, longitude } = stop.dealer;
      if (latitude && longitude && !isNaN(latitude) && !isNaN(longitude)) {
        lats.push(latitude);
        lngs.push(longitude);
      }
    })
  );
  if (lats.length === 0) return FLORIDA_CENTER;
  return [
    lats.reduce((a, b) => a + b, 0) / lats.length,
    lngs.reduce((a, b) => a + b, 0) / lngs.length
  ];
}

const AllRoutesMapComponent: React.FC<AllRoutesMapProps> = ({
  routes,
  focusedTechnicianId,
  fallbackBaseLocation,
  onRouteMetrics
}) => {
  const { resolvedTheme } = useTheme();
  const mapRef = useRef<L.Map | null>(null);

  const [showNavigationDialog, setShowNavigationDialog] = useState(false);
  const [focusedInstructions, setFocusedInstructions] = useState<RouteInstructions | null>(null);

  const center = useMemo(() => computeCenter(routes), [routes]);

  // Flatten all dealer locations for the bounds calculator
  const allDealerLocations = useMemo<DealerLocation[]>(
    () => routes.flatMap(r => r.stops.map(s => s.dealer)),
    [routes]
  );

  // Routes the bounds calculator should consider (cast to the shared map type)
  const mapRoutes = useMemo<TechnicianRoute[]>(() => routes, [routes]);

  const focusedRoute = useMemo(
    () => routes.find(r => r.technician_id === focusedTechnicianId) || null,
    [routes, focusedTechnicianId]
  );

  // Clear stale turn-by-turn instructions when focus leaves a route.
  useEffect(() => {
    if (!focusedRoute) setFocusedInstructions(null);
  }, [focusedRoute]);

  // Dark-mode background handling for the Leaflet container
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const container = map.getContainer();
    if (!container) return;
    const bgValue = getComputedStyle(document.documentElement)
      .getPropertyValue('--background')
      .trim();
    const bgColor = resolvedTheme === 'dark' ? `hsl(${bgValue})` : 'transparent';
    container.style.setProperty('background-color', bgColor, 'important');
  }, [resolvedTheme]);

  const baseFor = (route: BoardRoute) => route.startLocation || fallbackBaseLocation || null;

  return (
    <div
      className='h-full w-full bg-background dark:bg-background route-preview-map'
      style={{ position: 'relative' }}
    >
      <style>{`
        .dealer-name-tooltip {
          background-color: rgba(255, 255, 255, 0.95) !important;
          padding: 3px 8px !important;
          border-radius: 4px !important;
          font-size: 11px !important;
          font-weight: 600 !important;
          color: #1f2937 !important;
          box-shadow: 0 1px 3px rgba(0,0,0,0.2) !important;
          white-space: nowrap !important;
          font-family: Arial, sans-serif !important;
          border: none !important;
        }
        .dealer-name-tooltip::before { display: none !important; }
      `}</style>

      <MapContainer
        center={center}
        zoom={10}
        className='h-full w-full'
        style={{ height: '100%', width: '100%' }}
        ref={mapRef}
        zoomControl={true}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
        />

        <MapBoundsCalculator
          dealerLocations={allDealerLocations}
          routes={mapRoutes}
          baseLocation={null}
        />

        {(() => {
          const liveAll = routes.length <= LIVE_ALL_THRESHOLD;
          return routes.map(route => {
            const base = baseFor(route);
            if (!base || route.stops.length === 0) return null;
            // "In focus group" = nothing focused, or this is the focused route.
            const inFocusGroup =
              !focusedTechnicianId || route.technician_id === focusedTechnicianId;
            const isTheFocusedOne = route.technician_id === focusedTechnicianId;
            const renderLive = liveAll || isTheFocusedOne;

            if (renderLive) {
              return (
                <RoutingControl
                  key={route.routeId}
                  route={route}
                  baseLocation={base}
                  color={route.route_color}
                  dimmed={!inFocusGroup}
                  showTooltips={inFocusGroup}
                  onRouteUpdate={(distance, duration) => {
                    onRouteMetrics?.(route.routeId, distance, duration);
                  }}
                  {...(isTheFocusedOne ? { onInstructionsUpdate: setFocusedInstructions } : {})}
                />
              );
            }

            return (
              <StaticRoutePolyline
                key={route.routeId}
                route={route}
                baseLocation={base}
                color={route.route_color}
                dimmed={!!focusedTechnicianId}
              />
            );
          });
        })()}
      </MapContainer>

      {/* Turn-by-turn navigation for the focused technician */}
      <div className='absolute top-3 right-3 z-[1000]'>
        <button
          onClick={() => setShowNavigationDialog(true)}
          disabled={!focusedInstructions}
          title={
            focusedRoute
              ? `Turn-by-turn directions for ${focusedRoute.technicianName}`
              : 'Select a technician to view directions'
          }
          className='p-1 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-110 transition-transform'
        >
          <Navigation className='h-6 w-6 text-gray-700 drop-shadow-md' />
        </button>
      </div>

      <NavigationDirectionsDialog
        open={showNavigationDialog}
        onOpenChange={setShowNavigationDialog}
        instructions={focusedInstructions}
        route={focusedRoute}
      />
    </div>
  );
};

export const AllRoutesMap = React.memo(AllRoutesMapComponent);

export default AllRoutesMap;
